import { FindLastConversationsByContextQueryType, FindMessagesByConversationGQLQueryType } from './indeed.types';

export const findContactSummaryByCandidateIdGQLQuery = (candidateId: string) => ({
  operationName: 'ContactSummary',
  query:
    'query ContactSummary($input: TalentEngagementSummaryInput!) {\n  contactRecord: talentEngagementSummary(input: $input) {\n    permissions {\n      action\n      status\n      __typename\n    }\n    candidate {\n      id\n      email\n      phoneNumber\n      __typename\n    }\n    contactHistory: engagements {\n      contactType: type\n      date: created\n      dateStamp: created\n      email: senderEmail\n      contactedBy: senderId\n      contactOutcome: status\n      statusHistory {\n        status\n        date: modifiedDate\n        dateStamp: modifiedDate\n        __typename\n      }\n      __typename\n    }\n    __typename\n  }\n}\n',
  variables: {
    input: {
      candidateId: candidateId,
    },
  },
});

export const findConversationsByJobSeekerKey = (jobSeekerKey: string) => ({
  operationName: 'FindConversationsByContext',
  query: `
      query FindConversationsByContext(
        $contexts: [ConversationContext!],
        $scope: [ConversationScopeInput!],
        $before: String,
        $after: String,
        $last: Int = 50
      ) {
        findConversations(
          input: {
            filter: {
              contexts: $contexts,
              scope: $scope
            }
          },
          last: $last,
          before: $before,
          after: $after
        ) {
          conversations {
            ...ConversationSnippetFragment
            __typename
          }
          __typename
        }
      }
  
      fragment ConversationSnippetFragment on Conversation {
        id 
        __typename
      }
    `,
  variables: {
    contexts: ['RESUME_CONTACT'],
    last: 50,
    scope: [{ key: 'JOB_SEEKER_KEY', value: jobSeekerKey }],
  },
});

export const findLast50Conversations = (beforeToken: string | undefined) => {
  const mainGQLQuery: FindLastConversationsByContextQueryType = {
    operationName: 'FindConversationsByContext',
    query:
      'query FindConversationsByContext($contexts: [ConversationContext!], $scope: [ConversationScopeInput!], $before: String, $after: String, $last: Int = 50) {\n  findConversations(\n    input: {filter: {contexts: $contexts, includedLabels: [], excludedLabels: [], metadata: [], scope: $scope}}\n    last: $last\n    before: $before\n    after: $after\n  ) {\n    pageInfo {\n      ...FindConversationsPageInfoFragment\n      __typename\n    }\n    conversations {\n      ...ConversationSnippetFragment\n      __typename\n    }\n    __typename\n  }\n}\n\nfragment FindConversationsPageInfoFragment on PageInfo {\n  endCursor\n  hasNextPage\n  hasPreviousPage\n  startCursor\n  __typename\n}\n\nfragment ConversationSnippetFragment on Conversation {\n  id\n  context\n  lastEvent {\n    type\n    publicationDateTime\n    messageBody\n    messagePreview\n    id\n    attachments {\n      fileName\n      __typename\n    }\n    author {\n      accountKey\n      __typename\n    }\n    __typename\n  }\n  locks {\n    primary\n    reason\n    timestamp\n    __typename\n  }\n  userReadsInfo {\n    unreadCount\n    __typename\n  }\n  participants {\n    accountKey\n    externalParticipantId\n    participantName\n    role\n    __typename\n  }\n  scope {\n    key\n    value\n    __typename\n  }\n  userLabelInfo {\n    labels\n    __typename\n  }\n  __typename\n}\n',
    variables: {
      contexts: ['RESUME_CONTACT'],
      last: 50,
    },
  };

  if (beforeToken) {
    mainGQLQuery.variables.before = beforeToken;
  }

  return mainGQLQuery;
};

export const findMessagesByConversationGQLQuery = (
  conversationId: string,
  previousPageToken: string | undefined,
): FindMessagesByConversationGQLQueryType => {
  const mainGQLQuery: FindMessagesByConversationGQLQueryType = {
    operationName: 'GetConversationEvents',
    query:
      'query GetConversationEvents($conversationId: ID!, $previousPageToken: String, $nextPageToken: String, $timelineModuleInput: TimelineModuleInput! = {atk: "", telVersionUpperBound: ""}) {\n  conversation(input: {conversationId: $conversationId}) {\n    id\n    moderationStatus\n    ...ConversationEventsConnection\n    ...ConversationParticipants\n    userReadsInfo {\n      readCursor\n      lastVisited\n      unreadCount\n      __typename\n    }\n    __typename\n  }\n}\n\nfragment ConversationEventsConnection on Conversation {\n  eventsConnection(before: $previousPageToken, after: $nextPageToken) {\n    edges {\n      cursor\n      node {\n        ...ConversationEventListItem\n        ...TimelineModuleResult\n        __typename\n      }\n      __typename\n    }\n    pageInfo {\n      hasPreviousPage\n      hasNextPage\n      startCursor\n      endCursor\n      __typename\n    }\n    __typename\n  }\n  __typename\n}\n\nfragment ConversationEventListItem on ConversationEvent {\n  id\n  author {\n    accountKey\n    externalParticipantId\n    role\n    __typename\n  }\n  cleanedMessageBody\n  type\n  messageBody\n  messageContentFormat\n  messagePreview\n  metadata {\n    key\n    value\n    __typename\n  }\n  publicationDateTime\n  payload {\n    key\n    value\n    __typename\n  }\n  attachments {\n    contentHash\n    fileSid\n    fileName\n    fileExtension\n    __typename\n  }\n  subType\n  __typename\n}\n\nfragment TimelineModuleResult on ConversationEvent {\n  timelineModuleResult(input: $timelineModuleInput) {\n    ... on TimelineModule {\n      telContent\n      overrides {\n        message\n        sender\n        icon {\n          iflIconName\n          iflColorReference\n          __typename\n        }\n        __typename\n      }\n      data\n      timelineModuleType\n      __typename\n    }\n    ... on TimelineModuleError {\n      code\n      message\n      __typename\n    }\n    __typename\n  }\n  __typename\n}\n\nfragment ConversationParticipants on Conversation {\n  participants {\n    ...ConversationParticipant\n    __typename\n  }\n  __typename\n}\n\nfragment ConversationParticipant on ConversationParticipant {\n  accountKey\n  externalParticipantId\n  participantName\n  role\n  removed\n  employerUser {\n    account {\n      accountProfile {\n        email {\n          emailAddress\n          __typename\n        }\n        __typename\n      }\n      __typename\n    }\n    __typename\n  }\n  peripheralAccount {\n    accountProfile {\n      email {\n        emailAddress\n        __typename\n      }\n      __typename\n    }\n    __typename\n  }\n  accessLevel\n  __typename\n}\n',
    variables: {
      conversationId: conversationId,
    },
  };

  if (previousPageToken) {
    mainGQLQuery.variables.previousPageToken = previousPageToken;
  }

  return mainGQLQuery;
};
